import type { Database } from "@/integrations/supabase/types";

export type FormReadCorrection = Database["public"]["Tables"]["form_read_corrections"]["Row"];
export type FormReadCorrectionReason = FormReadCorrection["reason"];
export type CorrectionSummaryRow = Pick<
  FormReadCorrection,
  "id" | "encounter_id" | "reason" | "low_confidence" | "created_at"
>;

export const REASON_LABELS: Record<FormReadCorrectionReason, string> = {
  incorrect: "Incorrect",
  abstained: "Abstained",
  no_mark_found: "No mark found",
};

export const REASONS = Object.keys(REASON_LABELS) as FormReadCorrectionReason[];

export interface EncounterCorrections {
  encounterId: string;
  judgedAt: string;
  reasonCounts: Record<FormReadCorrectionReason, number>;
  lowConfidenceCount: number;
}

/** The replacement is assessed on every row that swaps the extractor's items: only an `abstained` row keeps them. */
export const asksReplacementQuestion = (row: Pick<FormReadCorrection, "reason">) => row.reason !== "abstained";

export const isAnswered = (row: Pick<FormReadCorrection, "reason" | "judge_was_right" | "replacement_was_right">) =>
  row.judge_was_right !== null && (!asksReplacementQuestion(row) || row.replacement_was_right !== null);

export const asksExpectedItems = (
  row: Pick<FormReadCorrection, "reason" | "judge_was_right" | "replacement_was_right">,
) => row.judge_was_right === false || (asksReplacementQuestion(row) && row.replacement_was_right === false);

export interface ItemChange {
  text: string;
  change: "kept" | "removed" | "added";
}

/** Only an `incorrect` row replaces the extractor's items; the other reasons leave them all in place. */
export const diffItems = (row: Pick<FormReadCorrection, "reason" | "extractor_items" | "corrected_items">) => {
  const replaced = asksReplacementQuestion(row);
  return {
    extractor: row.extractor_items.map(
      (text): ItemChange => ({
        text,
        change: !replaced || row.corrected_items.includes(text) ? "kept" : "removed",
      }),
    ),
    corrected: row.corrected_items.map(
      (text): ItemChange => ({ text, change: row.extractor_items.includes(text) ? "kept" : "added" }),
    ),
  };
};

export const QUESTIONS = {
  judge: {
    label: "Was the judge correct to flag this row?",
    hint: "The judge flagged this row as the extractor being incorrect. Answer Yes if the extractor really was wrong.",
  },
  replacement: {
    label: "Do the corrected items match the form?",
    hint: "The lists show printed options only. The judge's handwriting reading, when there is one, sits beside the page. Answer Yes if these items, plus any handwriting you can see, are what the form shows for this row.",
  },
};

export const NO_CORRECTED_ITEMS: Record<FormReadCorrectionReason, string> = {
  incorrect: "None listed: check the handwriting reading and the form",
  abstained: "No replacement: the judge could not vouch for this row",
  no_mark_found: "Nothing extracted: ticked Yes, no option clear",
};

/** Only recorded rows are known, so a later occurrence keeps its number even when it is the only one recorded. */
export const rowLabel = (
  row: Pick<FormReadCorrection, "row_family" | "family_ordinal">,
  pageRows: readonly Pick<FormReadCorrection, "row_family">[],
) =>
  row.family_ordinal > 0 || pageRows.filter((other) => other.row_family === row.row_family).length > 1
    ? `${row.row_family} ${row.family_ordinal + 1}`
    : row.row_family;

/** Encounters come out in the order they first appear, so rows must arrive newest first. */
export const groupByEncounter = (rows: readonly CorrectionSummaryRow[]): EncounterCorrections[] => {
  const byEncounter = new Map<string, EncounterCorrections>();
  for (const row of rows) {
    let group = byEncounter.get(row.encounter_id);
    if (!group) {
      group = {
        encounterId: row.encounter_id,
        judgedAt: row.created_at,
        reasonCounts: { incorrect: 0, abstained: 0, no_mark_found: 0 },
        lowConfidenceCount: 0,
      };
      byEncounter.set(row.encounter_id, group);
    }
    group.reasonCounts[row.reason] += 1;
    if (row.low_confidence) group.lowConfidenceCount += 1;
  }

  return [...byEncounter.values()];
};

export interface CorrectionPage {
  key: string;
  rawBundleId: string;
  pageKey: string;
  rows: FormReadCorrection[];
}

export const groupByPage = (rows: readonly FormReadCorrection[]): CorrectionPage[] => {
  const byPage = new Map<string, CorrectionPage>();
  for (const row of rows) {
    const key = `${row.raw_bundle_id}:${row.page_key}`;
    let page = byPage.get(key);
    if (!page) {
      page = { key, rawBundleId: row.raw_bundle_id, pageKey: row.page_key, rows: [] };
      byPage.set(key, page);
    }
    page.rows.push(row);
  }

  const pages = [...byPage.values()];
  for (const page of pages) {
    page.rows.sort((a, b) => a.row_family.localeCompare(b.row_family) || a.family_ordinal - b.family_ordinal);
  }
  return pages;
};
